const ANALYTICS_VALUE_LIMIT = 120
const ATTRIBUTION_VALUE_LIMIT = 200

const BLOCKED_ANALYTICS_KEYS = new Set([
  "name",
  "email",
  "phone",
  "company",
  "company_name",
  "brief",
  "description",
  "website_url",
  "page_url",
  "referrer",
  "hourly_cost",
  "exact_cost",
])

const blockedAnalyticsKey = key =>
  BLOCKED_ANALYTICS_KEYS.has(String(key).toLowerCase()) ||
  /(?:free_text|personal_data|access_details)/i.test(key)

const sanitizeText = (value, limit = ATTRIBUTION_VALUE_LIMIT) =>
  String(value ?? "")
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .trim()
    .slice(0, limit)

const sanitizeAnalyticsPayload = payload =>
  Object.entries(payload || {}).reduce((safe, [key, value]) => {
    if (blockedAnalyticsKey(key)) return safe
    if (!["string", "number", "boolean"].includes(typeof value)) return safe

    const cleanValue =
      typeof value === "string"
        ? sanitizeText(value, ANALYTICS_VALUE_LIMIT)
        : value

    if (cleanValue === "") return safe
    safe[sanitizeText(key, 60)] = cleanValue
    return safe
  }, {})

const sanitizeAttributionValue = value => sanitizeText(value)

const safeUrl = (value, keepAttributionQuery = false) => {
  if (!value) return ""

  try {
    const url = new URL(value)
    if (!["http:", "https:"].includes(url.protocol)) return ""

    url.username = ""
    url.password = ""
    url.hash = ""

    if (keepAttributionQuery) {
      const allowed = new Set([
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "gclid",
        "gbraid",
        "wbraid",
        "msclkid",
      ])
      Array.from(url.searchParams.keys()).forEach(key => {
        if (!allowed.has(key)) url.searchParams.delete(key)
      })
    } else {
      url.search = ""
    }

    return sanitizeText(url.toString(), 500)
  } catch (error) {
    return ""
  }
}

const getAttributionSource = attribution => {
  if (
    attribution?.gclid ||
    attribution?.gbraid ||
    attribution?.wbraid ||
    attribution?.msclkid
  ) {
    return "click_id"
  }
  if (attribution?.utm_source) return "utm"
  if (attribution?.referrer) return "referral"
  return attribution ? "direct" : "unknown"
}

const isAvailabilityVisible = (availability, now = new Date()) => {
  if (!availability?.enabled || !availability.label) return false
  if (!availability.effectiveDate) return true

  const effectiveDate = new Date(`${availability.effectiveDate}T23:59:59`)
  return !Number.isNaN(effectiveDate.getTime()) && effectiveDate >= now
}

const markOnce = (key, seen) => {
  if (!key || !seen || seen.has(key)) return false
  seen.add(key)
  return true
}

module.exports = {
  getAttributionSource,
  isAvailabilityVisible,
  markOnce,
  safeUrl,
  sanitizeAnalyticsPayload,
  sanitizeAttributionValue,
}
